import { Button, Empty, Table } from "antd";
import { useCallback, useEffect, useState } from "react";
import { ColumnType } from "antd/lib/table/interface";
import DateTimeStamp from "../misc/DateTimeStamp";
import "./BillingHistory.scss";
import {
  getInvoicePath,
  getUserFriendlyDescription,
} from "../../../service/BillingService";
import Price from "../misc/Price";
import {
  isIOS,
  openInNewTab,
  viewFileInNewTabUsingBlob,
} from "../../../util/utils";
import AccountsApi from "../../../api/AccountsApi";
import { useRecoilState } from "recoil";
import { memberState } from "../../../state/atoms";
import { error } from "../../../service/MessageService";
import { PurchaseDto } from "../../../generated/dashboard-client";

interface BillingHistoryParams {
  purchases: PurchaseDto[];
  loading?: boolean;
}

const locale = {
  emptyText: (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      imageStyle={{ height: 60 }}
      description={<span> No data yet to show</span>}
    />
  ),
};

export default function BillingHistory({
  purchases,
  loading,
}: BillingHistoryParams) {
  const [member] = useRecoilState(memberState);

  const [columns, setColumns] = useState([] as ColumnType<any>[]);
  const [data, setData] = useState([] as any[]);

  const openInvoice = useCallback(
    (id?: string) => {
      if (!member.id) return;

      if (id) {
        let win: Window | undefined = undefined;
        if (isIOS()) {
          win = window.open() || undefined;
        }
        // AccountsApi.getInvoice(member.id, id)
        AccountsApi.getReceipt(member.id, id)
          .then((resp) => {
            viewFileInNewTabUsingBlob(resp.request.response, `${id}.pdf`, win);
          })
          .catch(() => openInNewTab(getInvoicePath(id)));
      } else {
        error("Cannot open invoice at the moment.");
      }
    },
    [member.id]
  );

  function getTitle(key: keyof PurchaseDto): string {
    switch (key) {
      case "purchaseDate":
        return "Date";
      case "description":
        return "Description";
      case "price":
        return "Amount";
      default:
        return "";
    }
  }

  const getColumnContent = useCallback(
    (text: string, purchase: PurchaseDto, key: keyof PurchaseDto) => {
      switch (key) {
        case "purchaseDate":
          return <DateTimeStamp date={text} dateOnly={true} />;
        case "description":
          return <span>{getUserFriendlyDescription(text)}</span>;
        case "price":
          return (
            <span>
              <Price amount={text} />
            </span>
          );
        case "id":
          return (
            <>
              {purchase.transactionId && (
                <Button
                  className={"secondary"}
                  onClick={() => openInvoice(purchase.transactionId)}
                >
                  Receipt
                </Button>
              )}
              {!purchase.transactionId && <span>No invoice available.</span>}
            </>
          );
        case "transactionId":
          return <span></span>;
        default:
          return <span>{text}</span>;
      }
    },
    [openInvoice]
  );

  useEffect(() => {
    if (!purchases || !purchases.length) return;

    const tableColumns = Object.keys(purchases[0]).filter((it) => it !== "id");
    tableColumns.push("id");

    let isMobile = false;
    if (window.innerWidth <= 500) {
      isMobile = true;
    }

    setColumns(
      tableColumns
        .filter((it) => {
          if (isMobile) {
            return it !== "transactionId" && it !== "description";
          } else {
            return it !== "transactionId";
          }
        })
        .map((key) => {
          return {
            title: getTitle(key as keyof PurchaseDto),
            dataIndex: key,
            key,
            render: (text: string, purchase) =>
              getColumnContent(text, purchase, key as keyof PurchaseDto),
          };
        })
    );

    setData(
      purchases.map((it) => {
        return {
          key: it.id,
          id: it.id,
          purchaseDate: it.purchaseDate,
          price: it.price,
          description: it.description,
          transactionId: it.transactionId,
        };
      })
    );
  }, [purchases, getColumnContent]);

  return (
    <section className={"history"}>
      <h4>Billing History</h4>

      <Table
        locale={locale}
        className={"table"}
        loading={loading}
        columns={columns}
        dataSource={data}
        pagination={false}
      />
    </section>
  );
}
